import React, { useReducer, useState } from 'react'
import { useMutation } from '@apollo/client/react'
import { useRouter } from 'next/router'
import { path } from 'ramda'
import classNames from 'classnames'
import ReactModal from 'react-modal'
import Link from 'next/link'

import Input from '@/components/input'
import Button2 from '@/components/button'
import Button from '@/components/newButton/Button'
// Utils
import useMediaQuery from '@/utils/publicApi'

import {
  facebookSignupUrl,
  googleLoginUrl,
  googleSignupUrl,
  facebookLoginUrl,
  getDogApplication,
  saveRedirectURL,
  saveToken,
  getToken,
  getUserData,
} from '@utils'
import { ILoginCustomerResponse } from '@types'
import { CREATE_CUSTOMER, CUSTOMER_LOGIN } from '@graphql'
import s from './signupModal.module.scss'

const actions = {
  SET_FIRST_NAME: 'SET_FIRST_NAME',
  SET_LAST_NAME: 'SET_LAST_NAME',
  SET_EMAIL: 'SET_EMAIL',
  SET_PASSWORD: 'SET_PASSWORD',
  SET_PASSWORD_CONFIRM: 'SET_PASSWORD_CONFIRM',
  SET_TOS: 'SET_TOS',
  SET_ERROR: 'SET_ERROR',
}

const reducer = (state, action) => {
  switch (action.type) {
    case actions.SET_FIRST_NAME:
      return {
        ...state,
        firstName: action.payload,
      }
    case actions.SET_LAST_NAME:
      return {
        ...state,
        lastName: action.payload,
      }
    case actions.SET_EMAIL:
      return {
        ...state,
        email: action.payload,
      }
    case actions.SET_PASSWORD:
      return {
        ...state,
        password: action.payload,
      }
    case actions.SET_PASSWORD_CONFIRM:
      return {
        ...state,
        passwordConfirm: action.payload,
      }
    case actions.SET_TOS:
      return {
        ...state,
        tos: action.payload,
      }
    case actions.SET_ERROR:
      return {
        ...state,
        error: action.payload,
      }
  }
  return state
}

interface SignUpEmailProps extends React.HTMLAttributes<HTMLDivElement> {
  success: () => void
  hideNameFields?: boolean
}

export const SignUpEmail = ({ success, hideNameFields = false }: SignUpEmailProps) => {
  const application = getDogApplication()

  const initialValue = {
    firstName: application?.name || '',
    lastName: application?.lastName || '',
    email: application?.email || '',
    password: '',
    passwordConfirm: '',
    tos: false,
    error: '',
  }

  const [state, dispatch] = useReducer(reducer, initialValue)
  const [errorMessage, setErrorMessage] = useState(null)
  const [errorFields, setErrorFields] = useState(null)

  const [createCustomer] = useMutation(CREATE_CUSTOMER, {
    onCompleted: (response) => {
      const JWT = path(['createCustomer', 'jwt'], response)
      if (JWT) {
        saveToken(JWT)
        success()
        const token = getToken()
        const userData = getUserData(token)
        // GTM sign up
        if (window.dataLayer) {
          window.dataLayer.push({
            event: 'sign_up',
            user: {
              type: userData.type,
              name: userData.firstName,
              lastName: userData.lastName,
              email: userData.email,
            },
          })
        }
        // UA sign up
        if (window.ga?.getAll) {
          window.ga.getAll()[0].send('event', 'sign_up', {
            user: {
              type: userData.type,
              name: userData.firstName,
              lastName: userData.lastName,
              email: userData.email,
            },
          })
        }
      }
      setErrorMessage(path(['createCustomer', 'errorMessage'], response))
      setErrorFields(path(['createCustomer', 'errorList'], response))
    },
  })

  // -- Errors display --- //
  const placeholders = {
    name: '',
    lastName: '',
    email: '',
    password: '',
  }

  errorFields?.map((errorField) => {
    if (errorField.field === 'firstName') {
      placeholders.name = errorField.error
    } else if (errorField.field === 'lastName') {
      placeholders.lastName = errorField.error
    } else if (errorField.field === 'email') {
      placeholders.email = errorField.error
    } else if (errorField.field === 'password') {
      placeholders.password = errorField.error
    }
    return placeholders
  })

  return (
    <div className={s.formHolder}>
      <div
        className={classNames({
          [s.row]: true,
          [errorMessage && placeholders.name !== '' ? s.rowError : null]: true,
          [s.hideNameFields]: hideNameFields,
        })}
      >
        <Input
          placeholder={errorMessage ? placeholders.name : 'First name'}
          value={state.firstName}
          onChange={(e) => dispatch({ type: actions.SET_FIRST_NAME, payload: e.target.value })}
        />
      </div>

      <div
        className={classNames({
          [s.row]: true,
          [errorMessage && placeholders.lastName !== '' ? s.rowError : null]: true,
          [s.hideNameFields]: hideNameFields,
        })}
      >
        <Input
          placeholder={errorMessage ? placeholders.lastName : 'Last name'}
          value={state.lastName}
          onChange={(e) => dispatch({ type: actions.SET_LAST_NAME, payload: e.target.value })}
        />
      </div>

      <div
        className={classNames({
          [s.row]: true,
          [errorMessage && placeholders.email !== '' ? s.rowError : null]: true,
        })}
      >
        <Input
          placeholder={errorMessage ? placeholders.email : 'Email'}
          value={state.email}
          onChange={(e) => dispatch({ type: actions.SET_EMAIL, payload: e.target.value })}
        />
      </div>
      <div
        className={classNames({
          [s.row]: true,
          [errorMessage && placeholders.password !== '' ? s.rowError : null]: true,
        })}
      >
        <Input
          type="password"
          placeholder={errorMessage ? placeholders.password : 'Password'}
          value={state.password}
          onChange={(e) => dispatch({ type: actions.SET_PASSWORD, payload: e.target.value })}
        />
      </div>
      <div
        className={classNames({
          [s.row]: true,
          [errorMessage && placeholders.password !== '' ? s.rowError : null]: true,
        })}
      >
        <Input
          type="password"
          placeholder={errorMessage ? placeholders.password : 'Confirm password'}
          value={state.passwordConfirm}
          onChange={(e) =>
            dispatch({
              type: actions.SET_PASSWORD_CONFIRM,
              payload: e.target.value,
            })
          }
        />
      </div>
      <div
        className={s.checkboxHolder}
        onClick={() => dispatch({ type: actions.SET_TOS, payload: !state.tos })}
      >
        <div
          className={classNames({
            [s.checkbox]: true,
            [s.active]: state.tos,
          })}
        />
        <div className={s.checkboxText}>
          I have read the<Link href="/tos">Terms and Conditions</Link>
          <br />
          and a<Link href="/policy">Privacy Policy.</Link>
        </div>
      </div>
      <div className={s.lastrow}>
        <Button2
          theme="new-green-width"
          title="Create Account"
          onClick={() => {
            if (state.password === state.passwordConfirm && state.tos) {
              dispatch({ type: actions.SET_ERROR, payload: null })
              createCustomer({
                variables: {
                  customer: {
                    firstName: state.firstName,
                    lastName: state.lastName,
                    email: state.email,
                    password: state.password,
                  },
                },
              })
            } else {
              dispatch({
                type: actions.SET_ERROR,
                payload:
                  state.password !== state.passwordConfirm
                    ? 'Passwords do not match'
                    : !state.tos
                      ? 'Please review the Terms & Conditions to proceed'
                      : null,
              })
            }
          }}
        />
      </div>
      <div className={s.error}>{state.error}</div>
    </div>
  )
}

interface SignupModalProps {
  success: () => void
  successRedirect?: string
  title?: string
  subtitle?: string
}

const SignupModal = ({ success, successRedirect, title, subtitle }: SignupModalProps) => {
  const [withEmail, setWithEmail] = useState(false)
  const router = useRouter()
  return (
    <div className={s.signup}>
      <div className={s.formHolder}>
        <div className={s.title}>
          {withEmail ? 'Create new account' : title || 'Welcome to Mawoo Pets!'}
        </div>
        <div className={s.subtitle}>
          {withEmail
            ? 'Create a new Mawoo Pets account.'
            : subtitle || 'Create a new account or sign in below.'}
        </div>
        {withEmail ? (
          <SignUpEmail success={success} />
        ) : (
          <>
            <div className={s.row}>
              <a
                onClick={() => {
                  saveRedirectURL(successRedirect)
                }}
              >
                <Button
                  theme={['basic-black', 'login']}
                  leftImage="/signup/facebook_logo.svg"
                  title="Continue with Facebook"
                  onClick={() => router.push(facebookSignupUrl)}
                />
              </a>
            </div>
            <div className={s.row}>
              <a
                onClick={() => {
                  saveRedirectURL(successRedirect)
                }}
              >
                <Button
                  theme={['basic-black', 'login']}
                  leftImage="/google_iso.svg"
                  title="Continue with Google"
                  onClick={() => router.push(googleSignupUrl)}
                />
              </a>
            </div>
            <div className={classNames(s.row, s.rowOr)}>
              <div className={s.divider}>
                <div className={s.dividerLine}>
                  {' '}
                  <hr />
                </div>
                <div>
                  <p id={s.or}>or</p>
                </div>
                <div className={s.dividerLine}>
                  {' '}
                  <hr />
                </div>
              </div>
            </div>
            <div className={s.row}>
              <Button
                theme={['basic-black', 'login']}
                title="Continue with Email"
                onClick={() => setWithEmail(true)}
              />
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export const SigninModal = ({ success, successRedirect }) => {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [remember, setRemember] = useState(false)
  const [error, setError] = useState(false)
  const router = useRouter()
  const [login] = useMutation<ILoginCustomerResponse>(CUSTOMER_LOGIN, {
    onCompleted: (data) => {
      if (typeof window !== 'undefined' && data && data.loginCustomer && data.loginCustomer.jwt) {
        saveToken(data.loginCustomer.jwt)
        success()
      } else {
        const errorMessage = path(['loginCustomer', 'errorMessage'], data)
        setError(errorMessage)
      }
    },
  })

  return (
    <div className={s.login}>
      <div className={s.formHolder}>
        <div className={s.title}>Sign In!</div>
        <div className={s.subtitle}>Sign in with your existing account.</div>
        <div
          className={classNames({
            [s.row]: true,
            [error ? s.rowError : null]: true,
          })}
        >
          <Input
            placeholder="Email"
            autoComplete="username"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        <div
          className={classNames({
            [s.row]: true,
            [error ? s.rowError : null]: true,
          })}
        >
          <Input
            type="Password"
            placeholder="Password"
            autoComplete="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </div>
        <div className={s.error}>{error}</div>
        <div className={s.twoColumns}>
          <div className={s.checkboxHolder} onClick={() => setRemember(!remember)}>
            <div
              className={classNames({
                [s.checkbox]: true,
                [s.active]: remember,
              })}
            />
            <div className={s.checkboxText}>Remember me</div>
          </div>
          <div className={s.rememberHolder}>
            <Link href="/password/reset/customer">Forgot Password?</Link>
          </div>
        </div>
        <div className={s.lastrow}>
          <Button2
            theme="new-green-width"
            title="Sign In"
            onClick={() => login({ variables: { customer: { email, password } } })}
          />
          <div className={classNames(s.row, s.rowOr)}>
            <div className={s.divider}>
              <div className={s.dividerLine}>
                {' '}
                <hr />
              </div>
              <div>
                <p id={s.or}>or</p>
              </div>
              <div className={s.dividerLine}>
                {' '}
                <hr />
              </div>
            </div>
          </div>
          <div className={s.row}>
            <a
              onClick={() => {
                saveRedirectURL(successRedirect)
              }}
            >
              <Button
                theme={['basic-black', 'login']}
                leftImage="/signup/facebook_logo.svg"
                title="Continue with Facebook"
                onClick={() => router.push(facebookLoginUrl)}
              />
            </a>
          </div>
          <div className={s.row}>
            <a
              onClick={() => {
                saveRedirectURL(successRedirect)
              }}
            >
              <Button
                theme={['basic-black', 'login']}
                leftImage="/google_iso.svg"
                title="Continue with Google"
                onClick={() => router.push(googleLoginUrl)}
              />
            </a>
          </div>
        </div>
      </div>
    </div>
  )
}

interface ModalProps {
  isOpen: boolean
  close: () => void
  success: () => void
  successRedirect?: string
  title?: string
  subtitle?: string
}

const Modal = ({ isOpen, close, success, successRedirect, title, subtitle }: ModalProps) => {
  const isMobile = useMediaQuery('(max-width:600px)')
  const [isRegistered, setIsRegistered] = useState(false)
  if (typeof window !== 'undefined') {
    ReactModal.setAppElement('body')
  }
  // Hide Intercom when modal is open in mobile
  if (isOpen && isMobile) {
    window.LiveChatWidget.call('update', {
      hide_default_launcher: true,
    })
  }

  return (
    <ReactModal isOpen={isOpen} overlayClassName={s.modalOverlay} className={s.modal}>
      <div className={s.closeButton} onClick={() => close()}>
        <img src="/close_modal.svg" alt="close icon" />
      </div>
      <div className={s.content}>
        {!isRegistered ? (
          <SignupModal
            success={success}
            successRedirect={successRedirect}
            title={title}
            subtitle={subtitle}
          />
        ) : (
          <SigninModal success={success} successRedirect={successRedirect} />
        )}
        <div className={s.footer}>
          {!isRegistered ? 'Already have an account?' : 'Back to'}
          <div className={s.link} onClick={() => setIsRegistered(!isRegistered)}>
            {!isRegistered ? 'Sign In' : 'Sign Up'}
          </div>
        </div>
      </div>
      <div className={s.call}>
        <p>
          Having issues? <span>Call our team at</span> 1-888-488-7203
        </p>
      </div>
    </ReactModal>
  )
}

export default Modal

export const InlineSignup = ({ success, successRedirect, title, subtitle }) => {
  const [isRegistered, setIsRegistered] = useState(false)

  saveRedirectURL(successRedirect)
  return (
    <div className={s.content}>
      {!isRegistered ? (
        <SignupModal
          success={success}
          successRedirect={successRedirect}
          title={title}
          subtitle={subtitle}
        />
      ) : (
        <SigninModal success={success} successRedirect={successRedirect} />
      )}
      <div className={s.footer}>
        {!isRegistered ? 'Already have an account?' : 'Back to'}
        <div className={s.link} onClick={() => setIsRegistered(!isRegistered)}>
          {!isRegistered ? 'Sign In' : 'Sign Up'}
        </div>
      </div>
    </div>
  )
}
